import { CardContainer, CardTitle } from '../../StyledComponents/Card';
import CondorSpinner from '../CondorSpinner';
import { ButtonRules, StyledButton } from '@canopysecurity/component-library';
import { useState } from 'react';
import { StyledModal } from '../StyledModal';
import { ModalContent } from '../manage-tags/ModalContent';
import React from 'react';
import { useMutation } from '@tanstack/react-query';
import { disassociateTag } from '../../services/TagsService';
import { DeviceTagResponse } from '../../services/DevicesService';

interface DeviceTagProps {
  serialNumber: string;
  loading: boolean;
  error: Error | undefined | null;
  data: DeviceTagResponse | undefined;
  refetch: () => Promise<any>;
}

interface DisassociateModalState {
  isVisible: boolean;
  tagId: string | null;
  tagName: string | null;
}

const ConfirmationModal = React.memo(
  ({
    isVisible,
    onClose,
    onConfirm,
    tagName,
    serialNumber,
    loading,
    error,
  }: {
    isVisible: boolean;
    onClose: () => void;
    onConfirm: () => void;
    tagName: string | null;
    serialNumber: string;
    loading: boolean;
    error?: Error;
  }) => (
    <StyledModal
      isOpen={isVisible}
      onRequestClose={onClose}
      label="dissociate-tag-modal"
    >
      <ModalContent
        title="Dissociate Tag from Device"
        loading={loading}
        error={error}
        handleSubmit={onConfirm}
        submitDisabled={loading}
        handleClose={onClose}
        buttonLabels={{
          loading: 'Dissociating...',
          create: 'Confirm Dissociation',
        }}
      >
        <div>
          Are you sure you want to dissociate this tag: {tagName} from device{' '}
          {serialNumber}?
        </div>
      </ModalContent>
    </StyledModal>
  ),
);
ConfirmationModal.displayName = 'ConfirmationModal';
// There is a big problem here with the query. It only returns the tag ID and not the tag name,
// so either we need to modify the query to include the tag name, or we will need to fetch the tag details separately when displaying the tag name.
// i think its best to modify the query to include the tag name, which would have other benefits in the other components, but its a lot more
export const DeviceTags = (props: DeviceTagProps) => {
  const [modalState, setModalState] = useState<DisassociateModalState>({
    isVisible: false,
    tagId: null,
    tagName: null,
  });
  const [disassociateError, setDisassociateError] = useState<Error>();

  const { mutate, isPending: disassociateTagLoading } = useMutation({
    mutationFn: disassociateTag,
    onError: (error) => {
      setDisassociateError(error);
    },
  });

  const handleOpenModal = (tagId: string, tagName: string) => {
    setModalState({ isVisible: true, tagId, tagName });
  };

  const handleCloseModal = () => {
    setModalState({ isVisible: false, tagId: null, tagName: null });
  };

  const handleDisassociateTag = async () => {
    if (!modalState.tagId) return;

    mutate({ tagId: modalState.tagId, serialNumber: props.serialNumber });
    handleCloseModal();
    await props.refetch();
  };

  if (!props.data && !props.loading && !props.error) {
    return (
      <CardContainer>
        <CardTitle>Device Tags</CardTitle>
        <div>No tags associated with this device.</div>
      </CardContainer>
    );
  }

  if (props.loading) {
    return (
      <CardContainer>
        <CardTitle>Device Tags</CardTitle>
        <CondorSpinner />
      </CardContainer>
    );
  }

  if (props.error) {
    return (
      <CardContainer>
        <CardTitle>Device Tags</CardTitle>
        <div>Error loading device tags: {props.error.message}</div>
      </CardContainer>
    );
  }

  return (
    <>
      <CardContainer>
        <CardTitle>Device Tags</CardTitle>
        <div>
          {props.data &&
            props.data.results &&
            props.data.results.length > 0 && (
              <ul
                style={{ marginLeft: '1em', listStyleType: 'none', padding: 0 }}
              >
                {props.data.results
                  .filter((tag) => tag && tag.tagName)
                  .map((tag) => (
                    <li
                      key={tag?.tagName}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <span style={{ width: '70%' }}>{tag?.tagName}</span>
                      <span
                        style={{
                          flexBasis: '40%',
                          textAlign: 'right',
                          maxWidth: '230px',
                        }}
                      >
                        <StyledButton
                          onPress={() =>
                            handleOpenModal(
                              tag?.tagId ?? '',
                              tag?.tagName ?? '',
                            )
                          }
                          buttonRules={ButtonRules.primaryB}
                          disabled={disassociateTagLoading}
                        >
                          Dissociate
                        </StyledButton>
                      </span>
                    </li>
                  ))}
              </ul>
            )}
        </div>
      </CardContainer>
      <ConfirmationModal
        isVisible={modalState.isVisible}
        onClose={handleCloseModal}
        onConfirm={handleDisassociateTag}
        tagName={modalState.tagName}
        serialNumber={props.serialNumber}
        loading={disassociateTagLoading}
        error={disassociateError}
      />
    </>
  );
};
